import { indexBy, prop, omit } from 'ramda';

import { APPOINTMENT_MEDIAS_STATUS, APPOINTMENT_TASKS_STATUS } from 'constants/AppointmentConstants';
import * as appointmentActionTypes from 'modules/appointments/actionTypes';
import { PATCH_ADVICE_SUCCESS } from 'modules/checklists/actionTypes';
import { FETCH_VAULT_SUCCESS } from 'modules/lockers/actionTypes';
import { createReducer } from 'utils/ReduxUtils';

import * as actionTypes from '../../actionTypes';

const initialState = {};

const fetchAppointmentTasksReducer = (state, { payload }) => {
  const appointmentTasks = (payload.content ?? []).map(appointmentTask => ({
    ...(state?.[appointmentTask.id] ?? {}),
    ...appointmentTask,
  }));

  return {
    ...state,
    ...indexBy(prop('id'))(appointmentTasks),
  };
};

const fetchVaultRequestSuccessReducer = (state, { payload }) => {
  if ('nextAppointmentTask' in payload) {
    const { nextAppointmentTask: appointmentTask } = payload;
    return {
      ...state,
      [appointmentTask.id]: {
        ...(state[appointmentTask.id] ?? {}),
        ...appointmentTask,
      },
    };
  }
  return state;
};

const updateOne = (state, { payload }) => {
  const toUpdate = omit(['vault', 'appointment'], payload);
  return {
    ...state,
    [payload.id]: {
      ...(state[payload.id] ?? {}),
      ...toUpdate,
      ...(payload.vault ? { vaultId: payload.vault.id } : {}),
      attentionPoints: toUpdate?.attentionPoints ?? [],
    },
  };
};

const updateFromOtherSlice = (state, { payload }) => {
  const formatted =
    payload.appointmentTasks?.map(appointmentTask => ({
      ...omit(['vault', 'links'], appointmentTask),
      ...(appointmentTask?.vault ? { vaultId: appointmentTask.vault.id } : {}),
    })) ?? [];
  return fetchAppointmentTasksReducer(state, { payload: { content: formatted } });
};

const removeOne = (state, { payload }) => {
  const { [payload.id]: taskToRemove, ...tasks } = state;
  return { ...tasks };
};

const processAttentionPoints = task => {
  let attentionPoints = [...(task.attentionPoints ?? [])];
  const mustVerify = !task.task.medias.some(({ status }) => status === APPOINTMENT_MEDIAS_STATUS.PENDING);
  if (mustVerify) {
    attentionPoints = attentionPoints.filter(({ error }) => error !== 'VERIFY_DOCUMENTS');
  }

  return attentionPoints;
};

const deleteMedia = (state, { payload: { appointmentTaskId, mediaId } }) => {
  const task = { ...state[appointmentTaskId] };
  task.task = {
    ...task.task,
    medias: task.task.medias.filter(({ id }) => id !== mediaId),
  };

  return {
    ...state,
    [appointmentTaskId]: {
      ...task,
      attentionPoints: processAttentionPoints(task),
    },
  };
};

// Here we cancel the old task to match with backend model
// In detailled view cancelled tasks must be ignored
// In calendar view cancelled tasks must shown as regular tasks
const reschedule = (state, { payload, meta }) => {
  let newState = { ...state };
  const { id } = meta;

  const oldTask = Object.values(newState).find(task => task.id === id);
  newState = updateOne(newState, {
    payload: {
      ...oldTask,
      status: APPOINTMENT_TASKS_STATUS.CANCELED,
    },
  });

  return updateOne(newState, { payload });
};

const updateClientDocumentsTask = (state, { payload }) => {
  const { appointmentTask } = payload;
  if (appointmentTask) {
    return updateOne(state, { payload: appointmentTask });
  }
  return state;
};

export default createReducer(initialState, {
  [FETCH_VAULT_SUCCESS]: fetchVaultRequestSuccessReducer,

  [actionTypes.FETCH_LOCKER_TASKS_SUCCESS]: fetchAppointmentTasksReducer,

  [actionTypes.PUT_SUCCESS]: updateOne,
  [actionTypes.POST_SUCCESS]: updateOne,
  [actionTypes.FETCH_SUCCESS]: updateOne,
  [actionTypes.POST_VERIFY_SUCCESS]: updateOne,
  [actionTypes.POST_PREPARE_SUCCESS]: updateOne,
  [actionTypes.POST_UNPREPARE_SUCCESS]: updateOne,
  [actionTypes.ACKNOWLEDGE_ISSUE_SUCCESS]: updateOne,
  [actionTypes.POST_RESOLVE_ISSUE_SUCCESS]: updateOne,

  [actionTypes.RESCHEDULE_SUCCESS]: reschedule,

  [appointmentActionTypes.PUT_SUCCESS]: updateFromOtherSlice,
  [appointmentActionTypes.POST_SUCCESS]: updateFromOtherSlice,
  [appointmentActionTypes.PATCH_STATUS_SUCCESS]: updateFromOtherSlice,
  [appointmentActionTypes.FETCH_APPOINTMENT_SUCCESS]: updateFromOtherSlice,

  [actionTypes.DELETE_SUCCESS]: removeOne,

  [actionTypes.DELETE_MEDIA_SUCCESS]: deleteMedia,

  [PATCH_ADVICE_SUCCESS]: updateClientDocumentsTask,
});
